import React, { FC, useState } from 'react';
import { zonedTimeToUtc } from 'date-fns-tz';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { TrashIcon } from 'assets/icons/TrashIcon';
import EditPayRateFormikDatePicker from 'components/shared/forms/datePickers/EditPayRateFormikDatePicker';
import { EditPayRateFormValues } from 'components/members/editPayments/EditPayRateForm/EditPayRateForm';
import ValidationTextInput from 'components/shared/forms/ValidationTextInput';
import CustomFormikSelect from 'components/shared/forms/CustomFormikSelect';
import ConfirmDialog from 'components/shared/ConfirmDialog';
import Button from 'components/shared/Button';
import { MaxLength8Symbols } from 'constants/formsConstants';
import { RateHistoryEntry } from 'features/memberInfo/memberInfoTypes';
import { UserPayType } from 'features/user/userTypes';
import { dateToTimeZone } from 'helpers/dateToTimeZone';
import { setTimeInDate } from 'helpers/setTimeInDate';
import styles from 'components/members/editPayments/EditPaymentsForm.module.scss';
import { organizationInfoSelector } from 'features/organization/organizationSlice';
import { useSelector } from 'react-redux';

export interface EditRateHistoryEntryFormProps {
  entry: RateHistoryEntry;
  memberTimeZone: string;
  handleSubmit: (
    data: RateHistoryEntry,
    paymentRateType: 'billRate' | 'payRate'
  ) => void;
  handleDelete: (
    payAmountType: 'pay-rate' | 'bill-rate',
    entryId: number
  ) => void;
}

const EditPayRateHistoryEntryForm: FC<EditRateHistoryEntryFormProps> = ({
  entry,
  memberTimeZone,
  handleSubmit,
  handleDelete,
}) => {
  const organizationInfo = useSelector(organizationInfoSelector);

  const {
    id: entryId,
    type: payType,
    rate: payRate,
    startDate,
    status,
  } = entry;
  const [confirmDeleteEntry, setConfirmDeleteEntry] = useState(false);
  const InitialValues: EditPayRateFormValues = {
    payType: { value: payType, label: payType },
    payRate: payRate,
    startDate: dateToTimeZone(startDate, "UTC"),
  };

  const validationSchema = Yup.object({
    payRate: Yup.number()
      .typeError('You must specify a number')
      .test(
        'len',
        'Enter a smaller number',
        (val = 0) => val.toString().length <= MaxLength8Symbols
      ),
  });
  const payTypeOptions = (
    Object.keys(UserPayType) as Array<keyof typeof UserPayType>
  ).map((key) => ({
    value: key,
    label: key,
  }));

  const handleDeleteHistoryEntry = () => {
    handleDelete('pay-rate', entryId);
    setConfirmDeleteEntry(false);
  };

  return (
    <>
      <div className={styles.wrapper}>
        <Formik
          enableReinitialize
          initialValues={InitialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            const formattedStartDate = zonedTimeToUtc(
              setTimeInDate(values.startDate, 0, 0),
              "UTC"
            ).toISOString();
            handleSubmit(
              {
                id: entryId,
                type: values.payType.value,
                rate: values.payRate,
                startDate: formattedStartDate,
                status,
              },
              'payRate'
            );
            setSubmitting(false);
          }}
        >
          {(formik) => (
            <Form>
              <CustomFormikSelect
                label={'Pay type'}
                name="payType"
                options={payTypeOptions}
              />

              <div className={styles.rateFlex}>
                {formik.values.payType.value === UserPayType.Hourly && (
                  <ValidationTextInput
                    label="Pay rate"
                    name="payRate"
                    type="text"
                    placeholder={`Add pay rate ${organizationInfo.currency.symbol}/hr`}
                  />
                )}

                {formik.values.payType.value === UserPayType.Fixed && (
                  <ValidationTextInput
                    label="Fixed amount"
                    name="payRate"
                    type="text"
                    placeholder={organizationInfo.currency.symbol}
                  />
                )}

                <EditPayRateFormikDatePicker
                  label="Effective date"
                  name="startDate"
                  maxDate={new Date()}
                />
              </div>

              <div
                className={styles.submitWrapperEditEntryForm}
                style={{ marginBottom: 0 }}
              >
                <div className={styles.deleteBtn}>
                  <Button
                    type="button"
                    variant="delete"
                    onClick={() => setConfirmDeleteEntry(true)}
                  >
                    <TrashIcon />
                  </Button>
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={formik.isSubmitting}
                  preloader={formik.isSubmitting}
                >
                  Save changes
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <ConfirmDialog
        open={confirmDeleteEntry}
        title="Confirm"
        description="Delete this rate history entry? You will not be able to undo the changes."
        acceptBtnText="Delete"
        declineBtnText="Cancel"
        onClose={() => setConfirmDeleteEntry(false)}
        onAccept={handleDeleteHistoryEntry}
      />
    </>
  );
};

export default EditPayRateHistoryEntryForm;
